import {
  MANAGED_BY_P0,
  P0_TAG_NAME,
} from "@p0security/integrations-aws-shared/constants";
import { envSuffixFor } from "@p0security/integrations-aws-shared/policy/shared";
import type { DevEnv } from "@p0security/shared/types/environment";

import type { AwsOidcIdentityConfig } from "./types.ts";

// Note: Currently excluding additional SSH Permissions from here

export const p0OidcGrantsPathSegment = (context: DevEnv, installName: string) =>
  `p0-grants-oidc-${installName}${context.environmentType === "devLike" ? `-dev-${context.developer}` : ""}`;

export const p0OidcGrantsRolePath = (context: DevEnv, installName: string) =>
  `/${p0OidcGrantsPathSegment(context, installName)}/`;

const sanitizeForRoleName = (name: string) =>
  name.replace(/[^A-Za-z0-9+=,.@_-]/g, "-");

export const p0OidcGrantsRolePrefix = (context: DevEnv, installName: string) =>
  `P0OidcGrantsRole-${sanitizeForRoleName(installName)}${envSuffixFor(context)}`;

const p0OidcGrantsRole = (
  context: DevEnv,
  installName: string,
  suffix: string
) => `${p0OidcGrantsRolePrefix(context, installName)}${suffix}`;

const providerHostFor = (oidcProviderUrl: string) => {
  const url = new URL(oidcProviderUrl);
  return (url.host + url.pathname).replace(/\/$/, "");
};

const p0OidcGrantRoleTrustPolicy = ({
  accountId,
  awsPartition,
  oidcProviderUrl,
  audience,
}: AwsOidcIdentityConfig) => {
  const providerHost = providerHostFor(oidcProviderUrl);
  const providerArn = `arn:${awsPartition}:iam::${accountId}:oidc-provider/${providerHost}`;
  return {
    Version: "2012-10-17",
    Statement: [
      {
        Effect: "Allow",
        Principal: {
          Federated: providerArn,
        },
        Action: "sts:AssumeRoleWithWebIdentity",
        Condition: {
          StringEquals: {
            [`${providerHost}:aud`]: audience,
          },
        },
      },
    ],
  };
};

export const p0OidcGrantRoleCommands = (
  context: DevEnv,
  data: {
    item: AwsOidcIdentityConfig;
    installName: string;
    roleCount: number;
  }
) => {
  const { item, installName, roleCount } = data;
  const { oidcProviderUrl, audience } = item;
  const providerHost = providerHostFor(oidcProviderUrl);
  const trustPolicy = p0OidcGrantRoleTrustPolicy(item);
  // Fetches the OIDC provider's TLS certificate fingerprint, required by AWS to register the OIDC provider
  const thumbprint =
    `$(openssl s_client -connect ${providerHost}:443 -servername ${providerHost}` +
    ` </dev/null 2>/dev/null | openssl x509 -fingerprint -noout -sha1` +
    ` | sed 's/SHA1 Fingerprint=//' | sed 's/://g')`;

  return `# Create OIDC provider
aws iam create-open-id-connect-provider \\
  --url "${oidcProviderUrl}" \\
  --client-id-list "${audience}" \\
  --thumbprint-list "${thumbprint}" \\
  --tags Key="${P0_TAG_NAME}",Value="${MANAGED_BY_P0}"

# Create pool roles
for i in {0..${roleCount - 1}}; do
  aws iam create-role \\
  --tags Key="${P0_TAG_NAME}",Value="${MANAGED_BY_P0}" \\
  --role-name "${p0OidcGrantsRole(context, installName, "$i")}" \\
  --path "${p0OidcGrantsRolePath(context, installName)}" \\
  --assume-role-policy-document '${JSON.stringify(trustPolicy, undefined, 2)}'
  done`;
};

export const p0OidcGrantRoleTerraform = (
  context: DevEnv,
  data: {
    item: AwsOidcIdentityConfig;
    installName: string;
    roleCount: number;
  }
) => {
  const { item, installName, roleCount } = data;
  const { accountId, awsPartition, oidcProviderUrl, audience } = item;
  const providerHost = providerHostFor(oidcProviderUrl);

  return `locals {
  account_id = "${accountId}"
  oidc_provider_url = "${oidcProviderUrl}"
  audience = "${audience}"
  tags = {
    managed-by = "terraform"
    used-by    = "P0Security"
  }
}

data "tls_certificate" "oidc_provider" {
  url = local.oidc_provider_url
}

resource "aws_iam_openid_connect_provider" "p0_oidc_provider" {
  url             = local.oidc_provider_url
  client_id_list  = [local.audience]
  thumbprint_list = [data.tls_certificate.oidc_provider.certificates[0].sha1_fingerprint]
  tags            = local.tags
}

data "aws_iam_policy_document" "p0_oidc_grants_role_trust_policy" {
  statement {
    effect = "Allow"

    principals {
      type        = "Federated"
      identifiers = ["arn:${awsPartition}:iam::\${local.account_id}:oidc-provider/${providerHost}"]
    }

    actions = ["sts:AssumeRoleWithWebIdentity"]

    condition {
      test     = "StringEquals"
      variable = "${providerHost}:aud"
      values   = [local.audience]
    }
  }
}

# To import: terraform import "module.aws_p0_oidc_roles.aws_iam_role.p0_oidc_grants_roles[0]" {roleName}
resource "aws_iam_role" "p0_oidc_grants_roles" {
  count              = ${roleCount}
  name               = format("${p0OidcGrantsRole(context, installName, "%s")}", count.index)
  path               = "${p0OidcGrantsRolePath(context, installName)}"
  assume_role_policy = data.aws_iam_policy_document.p0_oidc_grants_role_trust_policy.json
  tags               = local.tags
}`;
};
